/**
 * Container component mounted on each main route/flow in the app (e.g.  mounted
 * at /transfer/request for request flow or /transfer/send for send flow. Renders
 * children depending on the full route/url (as defined in app.js),
 * which can be e.g. the recipient, preview and success pages
 */

import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import { handleErrorRedirect } from 'transfer/utils/pisp-utils'
import { withRouter } from 'react-router-dom'
import { isEmpty } from 'lodash'
import * as ViewActions from '../actions/Actions'
import * as OverpanelActions from '../actions/overpanels'
import { endCPLTracking, trackLink } from '../../lib/analytics/fpti'
import * as SendActions from '../actions/send'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import Loader from '../components/loader'
import { routeTo } from '../utils/routeHelper'
import {
  Overpanel,
  ErrorCommon,
  AddShippingAddressOverpanel,
  FundingOptionSelector,
  NotOverseasRecipient,
  BRRecipient,
  AboutCashAdvanceFees,
} from '../lazy-components'
import DeviceDataCollection from '../components/3DS2/DeviceDataCollection'
import {
  ERROR_COMPLIANCE_DENIED,
  UNAVAILABLE_FEATURE,
  SEND_MONEY_ERRORS,
  REQUEST_MONEY_ERRORS,
} from '../constants'
import { ENV_TYPE } from '../../../../lib/constants'

export class Page extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    dispatch: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    env: PropTypes.string.isRequired,
    overpanelError: PropTypes.object,
    pageErrorCode: PropTypes.string,
    shouldShowAddShippingAddressOverpanel: PropTypes.bool.isRequired,
    addShippingAddressOverpanelKey: PropTypes.string.isRequired,
    shouldShowFundingOptionsOverpanel: PropTypes.bool.isRequired,
    fundingOptionsOverpanelKey: PropTypes.string.isRequired,
    shouldShowErrorOverpanel: PropTypes.bool.isRequired,
    selectedFundingOption: PropTypes.object.isRequired,
    fundingOptions: PropTypes.array.isRequired,
    hasServerData: PropTypes.bool.isRequired, // i.e. serverProps exists or /config AJAX has completed
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    showRecipientFirst: PropTypes.bool,
    toCountryCode: PropTypes.string,
    amount: PropTypes.string,
    recipient: PropTypes.string,
    ddcCalls: PropTypes.array,
    fireDDC: PropTypes.bool,
    deviceDataCollectionUrl: PropTypes.string,
    showGenericAboutCashAdvanceFeesOverpanel: PropTypes.bool.isRequired,
    partnerName: PropTypes.string,
    canShowRetrySendText: PropTypes.bool,
    canShowAccountOverview: PropTypes.bool,
  }

  state = {
    isLoading: false,
    overpanelError: null,
    redirectOnError: false,
  }

  componentDidUpdate(prevProps) {
    const { overpanelError: newError } = this.props
    const { overpanelError: currentError } = prevProps

    // if there's a new overpanel error in the redux store
    if (!currentError && newError) {
      this.props.dispatch(OverpanelActions.toggleErrorOverpanel({ show: true }))
    }

    // push new loading states up to any iframes
    if (prevProps.isLoading !== this.props.isLoading) {
      this.props.dispatch(
        ViewActions.triggerExternalHook('loadingStateChanged', {
          isLoading: this.props.isLoading,
        })
      )
    }

    // if there was an error but it was just cleared from the redux store
    if (currentError && !newError) {
      this.closeErrorOverpanel()
    }
  }

  componentDidMount() {
    // Important check for external routes using redux — in homepage-external-redirect.js we initiate a fetch funding options
    // because we have all the data necessary from the URL. Because of this isLoading is true when this component mounts.
    // We need to make sure to fire the event to the external partner if that's the case, otherwise they'll only get the isLoading = false event.
    if (this.props.isLoading) {
      this.props.dispatch(
        ViewActions.triggerExternalHook('loadingStateChanged', {
          isLoading: true,
        })
      )
    }
  }

  closeErrorOverpanel = () => {
    this.props.dispatch(OverpanelActions.toggleErrorOverpanel({ show: false }))

    // get the first field with an error message and focus in on it
    const errorInput = document.querySelector(
      '.vx_has-error-with-message input'
    )
    if (errorInput) {
      errorInput.focus()
    }
  }

  getOverpanelErrorHeader = errorKey => {
    switch (errorKey.code) {
      case 'amexInsufficientFunds':
      case 'amexSuspectedFraud':
      case 'errorTransactionDeclinedNegativeBalance':
      case SEND_MONEY_ERRORS.GROUPS_SENDER_CANNOT_SEND_PERSONAL_PAYMENT:
      case SEND_MONEY_ERRORS.GROUP_CONTRIBUTION_LIMIT_EXCEEDED:
      case REQUEST_MONEY_ERRORS.REQUESTEE_SEARCH_PREFERENCES_NOT_ENABLED:
        return 'CUSTOM_ERROR'
      case 'requesteeSearchPreferencesNotEnabled':
        return 'requestOffTitle'
      default:
        return null
    }
  }

  getOverpanelErrorStatus = errorKey => {
    // when server error handling is cleaned up, add more cases with better rules (e.g. status code 400s are warning, etc.)
    switch (errorKey.code) {
      case 'internalServerError':
      case UNAVAILABLE_FEATURE:
        return 'CRITICAL'
      default:
        return 'WARNING'
    }
  }

  // when an error(code "errorSenderCountryDontSupportMobilePayments") happens,
  // redirect user to recipient page (different urls for send/buy flow, unified, xb, xb recipient first)
  routeToRecipientPage = () => {
    if (!this.state.redirectOnError) {
      return
    }

    routeTo(`${this.props.location.pathname}/..`)
  }

  handleCloseErrorOverPanel = () => {
    const {
      env,
      partnerName,
      overpanelError: { category },
    } = this.props
    this.closeErrorOverpanel()

    if (env === ENV_TYPE.PISP && partnerName === 'AMEX') {
      handleErrorRedirect(category)
    }
  }

  handleLetsTryAgainErrorOverPanel = () => {
    this.props.dispatch(SendActions.handleSendSubmit())
  }

  renderErrorOverpanel() {
    let customErrorComponent = null
    let onClosed = this.routeToRecipientPage
    let overPanelErrorHeader = null
    const overPanelErrorExtraData = this.props.overpanelError?.extra || {}
    if (this.props.overpanelError) {
      switch (this.props.overpanelError.customComponent.name) {
        case 'br-recipient':
          customErrorComponent = (
            <BRRecipient
              currentLocation={this.props.location.pathname}
              recipientEmail={this.props.recipient}
            />
          )
          break
        default:
          customErrorComponent = null
      }

      if (this.props.overpanelError.onClosed) {
        onClosed = this.props.overpanelError.onClosed
      }
      overPanelErrorHeader = this.getOverpanelErrorHeader(
        this.props.overpanelError
      )
    }

    let serviceTextKey = ''

    if (this.props.canShowRetrySendText) {
      serviceTextKey = 'content.retry.service'
    }
    if (this.props.canShowAccountOverview) {
      serviceTextKey = 'content.accountOverview.service'
    }

    return (
      <Overpanel
        show={this.props.shouldShowErrorOverpanel}
        handleClose={this.handleCloseErrorOverPanel}
        overpanelID="overpanel-p2pErrors"
        onClosed={onClosed}
      >
        {// if error exists, render error component within already rendered overpanel
        this.props.overpanelError &&
          !this.props.overpanelError.customComponent.use && (
            <ErrorCommon
              env={this.props.env}
              hideServiceMessage={this.props.overpanelError.hideServiceMessage}
              customErrorCode={this.props.overpanelError.code}
              {...(overPanelErrorHeader && { header: overPanelErrorHeader })}
              {...(overPanelErrorExtraData && {
                extra: overPanelErrorExtraData,
              })}
              message={this.props.overpanelError.renderedMessage}
              status={this.getOverpanelErrorStatus(this.props.overpanelError)}
              rawErrorCode={this.props.overpanelError.rawErrorCode}
              handleBackButton={this.handleCloseErrorOverPanel}
              handleLetsTryAgainButton={this.handleLetsTryAgainErrorOverPanel}
              serviceTextKey={serviceTextKey}
              showDismissButton={this.props.overpanelError.showDismissButton}
              handleDismissButton={this.handleCloseErrorOverPanel}
            />
          )}
        {customErrorComponent}
      </Overpanel>
    )
  }

  handleCloseAddShippingAddressOverpanel = () => {
    // document.querySelector('#pay-request-send-money-button').focus() // TODO: figure out how to handle a11y focusing for all flows (this is a shareable, reusable component)
    this.props.dispatch(
      OverpanelActions.toggleAddShippingAddressOverpanel({ show: false })
    )
  }

  handleEscShippingAddressOverpanel = e => {
    if (e.keyCode === 27) {
      this.props.dispatch(
        OverpanelActions.toggleAddShippingAddressOverpanel({ show: false })
      )
    }
  }

  renderAddShippingAddressOverpanel = () => {
    return (
      <Overpanel
        show={this.props.shouldShowAddShippingAddressOverpanel}
        handleClose={this.handleCloseAddShippingAddressOverpanel}
        overpanelID="overpanel-addShippingAddress"
      >
        <AddShippingAddressOverpanel
          key={this.props.addShippingAddressOverpanelKey}
        />
      </Overpanel>
    )
  }

  handleLoadingClassToggle = () => {
    if (!this.props.isLoading) {
      endCPLTracking() // todo move this to a reducer or somewhere more obvious IT'S REALLY IMPORTANT
    }

    return this.props.isLoading
  }

  renderErrorPage = () => {
    switch (this.props.pageErrorCode) {
      case 'not-overseas-recipient':
        return <NotOverseasRecipient />
      case 'br-recipient':
        return (
          <BRRecipient
            currentLocation={this.props.location.pathname}
            recipientEmail={this.props.recipient}
          />
        )
      default:
        return null
    }
  }

  handleCloseFundingOptionsOverpanel = () => {
    trackLink('close', 'link') // NOSONAR
    this.props.dispatch(
      OverpanelActions.toggleFundingOptionsOverpanel({ show: false })
    )
  }

  renderFundingOptionsOverpanel = () => {
    return (
      <Overpanel
        show={this.props.shouldShowFundingOptionsOverpanel}
        handleClose={this.handleCloseFundingOptionsOverpanel}
        overpanelID="overpanel-addCi"
      >
        <FundingOptionSelector
          isRenderedInOverpanel={true}
          key={this.props.fundingOptionsOverpanelKey}
          dispatch={this.props.dispatch}
        />
      </Overpanel>
    )
  }

  renderAboutCashAdvanceFeesOverpanel = () => {
    return (
      <Overpanel
        show={this.props.showGenericAboutCashAdvanceFeesOverpanel}
        handleClose={() => {
          tracking(
            Events.p2p_fi_warning_close_pressed({
              fi_warning_type: 'cash_advance',
            })
          )
          trackLink('close', 'link')
          this.props.dispatch(
            OverpanelActions.toggleCashAdvanceFeesOverpanel({
              show: false,
              type: 'ABOUT',
            })
          )
        }}
        overpanelID="overpanel-cashAdvanceFees"
      >
        <AboutCashAdvanceFees />
      </Overpanel>
    )
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {}
    const { overpanelError: newError } = props
    const { overpanelError: currentError } = state

    if (currentError !== newError) {
      newState.overpanelError = newError
    }
    if (state.isLoading !== props.isLoading) {
      newState.isLoading = props.isLoading
    }

    // if user closes this specific error overpanel
    if (
      !newError &&
      currentError &&
      currentError.code === 'errorSenderCountryDontSupportMobilePayments'
    ) {
      // remember to redirect after close the overpanel
      newState.redirectOnError = true
    } else if (state.redirectOnError) {
      newState.redirectOnError = false
    }

    if (!isEmpty(newState)) {
      return newState
    }

    return null
  }

  render() {
    if (!this.props.hasServerData) {
      return <Loader show={true} />
    }

    const errorPage = this.renderErrorPage()

    return (
      <div>
        <Loader show={this.handleLoadingClassToggle()} />

        {!errorPage ? this.props.children : errorPage}

        {/* shared overpanels, order matters here!! The latter ones can show on top of previous ones */}
        {this.renderFundingOptionsOverpanel()}
        {this.renderAboutCashAdvanceFeesOverpanel()}
        {this.renderAddShippingAddressOverpanel()}
        {this.renderErrorOverpanel()}
        <DeviceDataCollection dispatch={this.props.dispatch} isGuest={false} />
      </div>
    )
  }
}

export default withRouter(
  connect(
    ({
      server,
      loading,
      errors,
      overpanels,
      transaction,
      user,
      crossBorder,
      amount,
      recipient,
    }) => ({
      hasServerData: server.hasReceivedData,
      isLoading: loading.isLoading || !server.hasReceivedData,
      env: server.env,
      overpanelError: errors.overpanel,
      pageErrorCode: errors.page,
      showRecipientFirst: crossBorder.showRecipientFirst,
      toCountryCode: crossBorder.toCountryCode,
      amount: amount.amount,

      // overpanels controls
      shouldShowAddShippingAddressOverpanel: overpanels.addShippingAddress,
      addShippingAddressOverpanelKey: overpanels.addShippingAddressOverpanelKey,
      fundingOptionsOverpanelKey: overpanels.fundingOptionsOverpanelKey,
      shouldShowFundingOptionsOverpanel: overpanels.fundingOptionsOverpanel,
      shouldShowErrorOverpanel: overpanels.error,
      showGenericAboutCashAdvanceFeesOverpanel:
        overpanels.showGenericAboutCashAdvanceFeesOverpanel,

      // for funding options sidepanel
      selectedFundingOption: transaction.selectedFundingOption,
      fundingOptions: transaction.fundingOptions,
      partnerName: server.recipientData.partnerName,
      recipient: recipient.recipient,
      ddcCalls: transaction.ddcCalls,
      fireDDC: transaction.fireDDC,
      canShowRetrySendText:
        server.isLegalEntityUser &&
        [ERROR_COMPLIANCE_DENIED].includes(
          errors && errors.overpanel && errors.overpanel.code
        ),
      canShowAccountOverview:
        server.isLegalEntityUser &&
        [UNAVAILABLE_FEATURE].includes(
          errors && errors.overpanel && errors.overpanel.code
        ),
    })
  )(Page)
)
